<template>
  <div class="same-color-balls">
    <div class="axis-section">
      <div class="axis-label">{{ $t('betting.ballColor') }}</div>
      <div class="color-row">
        <button
          v-for="color in colorSlugs"
          :key="color"
          type="button"
          class="color-btn"
          :class="{ selected: selectedColor === color }"
          :style="getColorButtonStyle(color, selectedColor === color)"
          @click="selectColor(color)"
        >
          {{ BALL_COLOR_STYLES[color].label }}
        </button>
      </div>
    </div>

    <div class="axis-section">
      <div class="axis-label">{{ $t('betting.atLeast') }}</div>
      <div class="count-row">
        <button
          v-for="count in counts"
          :key="count"
          type="button"
          class="count-btn"
          :class="{ selected: selectedCount === count }"
          @click="selectCount(count)"
        >
          <span class="count-value">{{ count }}</span>
          <span class="count-odds">{{ formatOdds(countOdds[count]) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Market, MarketOutcome } from '@/stores/game'
import {
  BALL_COLOR_ORDER,
  BALL_COLOR_STYLES,
  matchesSameColorOutcome,
  normalizeSpecifier,
  type BallColorSlug,
} from '@/constants/sameColorBalls'
import { formatOdds } from '@/utils/currency'

const props = defineProps<{
  market: Market
}>()

const emit = defineEmits<{
  (e: 'select', outcome: MarketOutcome): void
}>()

const selectedColor = ref<BallColorSlug | null>(null)
const selectedCount = ref<string | null>(null)

const colorSlugs = BALL_COLOR_ORDER

const counts = computed(() => {
  const values = props.market.outcomes
    .map((outcome) => normalizeSpecifier(outcome.specifier))
    .filter((value) => value !== '')
  return [...new Set(values)].sort((a, b) => Number(a) - Number(b))
})

const countOdds = computed(() => {
  const map: Record<string, string> = {}
  for (const outcome of props.market.outcomes) {
    const specifier = normalizeSpecifier(outcome.specifier)
    if (specifier) {
      map[specifier] = outcome.odds
    }
  }
  return map
})

const resolvedOutcome = computed(() => {
  if (!selectedColor.value || !selectedCount.value) {
    return null
  }

  return props.market.outcomes.find((outcome) =>
    matchesSameColorOutcome(outcome, selectedColor.value!, selectedCount.value!)
  ) ?? null
})

function getColorButtonStyle(color: BallColorSlug, isSelected: boolean) {
  const style = BALL_COLOR_STYLES[color]
  if (isSelected) {
    return {
      backgroundColor: style.background,
      borderColor: style.border,
      color: style.text,
    }
  }

  return {
    backgroundColor: 'var(--bg-secondary, #fff)',
    borderColor: style.border,
    color: 'var(--text-primary)',
  }
}

function selectColor(color: BallColorSlug) {
  selectedColor.value = color
  tryAddBet()
}

function selectCount(count: string) {
  selectedCount.value = normalizeSpecifier(count)
  tryAddBet()
}

function tryAddBet() {
  if (resolvedOutcome.value) {
    emit('select', resolvedOutcome.value)
    selectedColor.value = null
    selectedCount.value = null
  }
}
</script>

<script lang="ts">
export default {
  name: 'SameColorBallsSelector',
}
</script>

<style scoped>
.same-color-balls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.axis-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.axis-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
}

.color-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.color-btn {
  flex: 1 1 calc(25% - 0.5rem);
  min-width: 4.5rem;
  padding: 0.55rem 0.35rem;
  border: 2px solid;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.15s ease;
}

.color-btn:hover {
  opacity: 0.9;
}

.count-row {
  display: flex;
  gap: 0.5rem;
}

.count-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  min-height: 3rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--text-primary);
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.count-btn:hover {
  border-color: var(--bg-danger);
}

.count-btn.selected {
  border-color: var(--bg-danger);
  background: rgba(255, 51, 21, 0.15);
  color: var(--text-primary);
}

.count-value {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
}

.count-odds {
  font-size: 0.65rem;
  font-weight: 500;
  opacity: 0.85;
}

@media (min-width: 769px) {
  .color-row {
    flex-wrap: nowrap;
    gap: 0.35rem;
  }

  .color-btn {
    flex: 1 1 0;
    min-width: 0;
    padding: 0.45rem 0.15rem;
    font-size: 0.65rem;
  }
}

@media (max-width: 768px) {
  .color-btn {
    flex: 1 1 calc(33% - 0.5rem);
    font-size: 0.65rem;
    min-width: 3.5rem;
  }

  .count-btn {
    min-height: 2.75rem;
  }
}
</style>
